import { FC, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, TextField, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { TFilters } from 'redux/types';
import { getPgRecurringCyclesRequest } from 'redux/reducers/pgSettings/reducer';
import {
	selectPgRecurringCycles,
	selectPgRecurringCyclesLoading,
} from 'redux/reducers/pgSettings/selectors';
import { TPgListRequest, TPgRecurringCycle } from 'services/api/paymentGateway/types';
import {
	ESortDirection,
	ETableStructureAlign,
	ETableStructureType,
	TTableStructure,
	TTableStructureColors,
	TTableStructureFilter,
} from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { usePgListLoader } from '../common/usePgListLoader';
import { PG_CYCLE_STATE_LABELS } from '../constants';

type TCycleColumn = TTableStructure<keyof TPgRecurringCycle, TPgRecurringCycle>;

const STATE_FILTER: TTableStructureFilter = Object.entries(PG_CYCLE_STATE_LABELS).map(
	([value, label]) => [value, label],
);
const STATE_COLORS: TTableStructureColors = [
	['pending', 'info.light'],
	['dd_pending', 'info.light'],
	['card_retrying', 'warning.light'],
	['succeeded', 'success.light'],
	['exhausted', 'error.light'],
	['dd_failed', 'error.light'],
	['no_method', 'warning.light'],
	['skipped', 'warning.light'],
];

const column = (
	name: string,
	selector: keyof TPgRecurringCycle,
	type: ETableStructureType,
	extra: Partial<TCycleColumn> = {},
): TCycleColumn => ({
	name,
	selector,
	show: true,
	sortable: false,
	filterable: false,
	type,
	align: ETableStructureAlign.LEFT,
	...extra,
});

const TABLE_STRUCTURE: TCycleColumn[] = [
	column('Month', 'cycleMonth', ETableStructureType.DATE_MONTH, { sortable: true }),
	column('Client', 'clientNumber', ETableStructureType.STRING, {
		sortable: true,
		searchable: true,
	}),
	column('Invoice', 'invoiceNumber', ETableStructureType.STRING, {
		sortable: true,
		value: (item) => String(item.invoiceNumber),
	}),
	column('Service', 'serviceCode', ETableStructureType.STRING, { searchable: true }),
	column('State', 'state', ETableStructureType.STATUS, {
		sortable: true,
		filterable: true,
		filter: STATE_FILTER,
		colors: STATE_COLORS,
	}),
	column('Card days tried', 'cardDaysAttempted', ETableStructureType.NUMBER, {
		sortable: true,
		align: ETableStructureAlign.RIGHT,
	}),
	column('Last card attempt', 'lastCardAttemptDate', ETableStructureType.DATE),
	column('DD requested', 'ddAttemptedAt', ETableStructureType.DATE_TIME),
	column('Failure e-mail', 'failureEmailSentAt', ETableStructureType.DATE_TIME),
	column('Skip reason', 'skipReason', ETableStructureType.STRING),
	column('Last error', 'lastError', ETableStructureType.STRING, {
		tooltip: (item) => item.lastError ?? '',
	}),
	column('Updated', 'updatedAt', ETableStructureType.DATE_TIME, { sortable: true }),
];

const currentMonth = () => DateTime.utc().toFormat('yyyy-MM');

/** Invoices of one monthly cycle with their state (03 §4.6). */
export const CyclesList: FC = () => {
	const dispatch = useDispatch();
	const cycles = useSelector(selectPgRecurringCycles);
	const loading = useSelector(selectPgRecurringCyclesLoading);
	const [month, setMonth] = useState(currentMonth);
	const [tableStructure, setTableStructure] = useState(TABLE_STRUCTURE);

	const extraFilters = useMemo<TFilters>(
		() =>
			month
				? [{ fieldName: 'cycleMonth', name: 'cycleMonth', value: `${month}-01`, operator: '=' }]
				: [],
		[month],
	);

	const load = useCallback(
		(request: TPgListRequest) => dispatch(getPgRecurringCyclesRequest(request)),
		[dispatch],
	);
	usePgListLoader(TABLE_STRUCTURE, load, {
		extraFilters,
		defaultOrder: { fieldName: 'updatedAt', direction: ESortDirection.DESC },
	});

	return (
		<EasTableWithFilter<TPgRecurringCycle>
			tableStructure={tableStructure}
			items={cycles}
			itemsIsLoading={loading}
			handleAction={() => undefined}
			onTableStructureChange={setTableStructure}
			skeletonRows={Array.from(new Array(5))}
		>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
				<Typography variant="body1" color="primary" fontWeight="bold">
					Cycle of the month
				</Typography>
				<TextField
					size="small"
					type="month"
					label="Month"
					value={month}
					onChange={(e) => setMonth(e.target.value)}
					InputLabelProps={{ shrink: true }}
					sx={{ backgroundColor: 'background.paper' }}
				/>
			</Box>
		</EasTableWithFilter>
	);
};
